import React from 'react';
import { useTheme } from 'styled-components';
import { WhatsappLogo, X as CloseIcon } from 'phosphor-react';
import { useParams } from 'react-router-dom';
import { MetroSpinner } from 'react-spinners-kit';

import { EHeadingSize } from '@domain/enums/components/EHeading';
import { IParams } from '@domain/interfaces/IParams';
import { IAddWhatsappInstanceModalProps } from '@domain/interfaces/dashboard/Automations/Canvas/SaveAutomationModal/IAddWhatsappInstanceModal';

import { useSocket } from '@helpers/hooks/pages/dashboard/whatsappInstance/useSocket';
import { useToast } from '@helpers/hooks/useToast';
import { useAutomation } from '@helpers/hooks/pages/dashboard/automation/useAutomation';

import AndroidWhatsappConfigImg from '@assets/dashboard/whatsapp/android-whatsapp-config.svg';
import IOSWhatsappConfigImg from '@assets/dashboard/whatsapp/ios-whatsapp-config.svg';

import * as S from './styles';

const AddWhatsappInstanceModal: React.FC<IAddWhatsappInstanceModalProps> = ({ isOpen, toggle }) => {
  const { colors } = useTheme();

  const { toast } = useToast();
  const { storeAliasId } = useParams<IParams>();
  const { socket } = useSocket();
  const {
    handleSetWhatsappInstanceAliasId,
    handleShowRenameWhatsappInstanceModal,
  } = useAutomation();

  const [imageURL, setImageURL] = React.useState<string>('');
  const [isLoadingImageURL, setIsLoadingImageURL] = React.useState<boolean>(true);

  const initConection = React.useCallback(() => {
    socket.emit('qrCode:request', { store_alias_id: storeAliasId });
  }, [socket, storeAliasId]);

  const getQRCodeData = React.useCallback(() => {
    socket.on('qrCode:read', data => {
      if (data.image) {
        setIsLoadingImageURL(false);
        setImageURL(data.image);
      }
    });
  }, [socket]);

  const handleSuccess = React.useCallback(() => {
    socket.on('qrCode:success', data => {
      if (data) {
        handleSetWhatsappInstanceAliasId(data.whatsapp_credential.alias_id);
        handleShowRenameWhatsappInstanceModal();
      }
    });
  }, [socket, handleShowRenameWhatsappInstanceModal, handleSetWhatsappInstanceAliasId]);

  const handleFailure = React.useCallback(() => {
    socket.on('qrCode:error', data => {
      if (data) {
        toast.error(data.message);
      }
    });
  }, [socket, toast]);

  React.useEffect(() => {
    if (socket) {
      initConection();
      getQRCodeData();
      handleSuccess();
      handleFailure();
    }
  }, [socket, initConection, getQRCodeData, handleSuccess, handleFailure]);

  return (
    <S.Wrapper isOpen={isOpen} toggle={toggle}>
      <S.HeaderWrapper>
        <S.GroupTitle>
          <WhatsappLogo size={28} color={colors.gray[1]} />
          <S.Title>Nova instância de Whatsapp</S.Title>
        </S.GroupTitle>
        <S.CloseButton onClick={toggle}>
          <CloseIcon size={20} color={colors.gray[3]} />
        </S.CloseButton>
      </S.HeaderWrapper>

      <S.Content>
        <S.HeadingAndStepsWrapper>
          <S.Heading type={EHeadingSize.H3}>
            Para criar uma nova <mark>instância do Whatsapp</mark>:
          </S.Heading>

          <S.StepsWrapper>
            <S.StepText>1. Abra o Whatsapp no seu celular.</S.StepText>

            <S.StepText>
              2. Toque em <strong>Mais opções</strong>
              <S.WhatsappConfigImg
                src={AndroidWhatsappConfigImg}
                alt="profitfy-android-whatsapp-config"
              />
              ou <strong>Configurações</strong>
              <S.WhatsappConfigImg src={IOSWhatsappConfigImg} alt="profitfy-ios-whatsapp-config" />e
              selecione <br /> <strong>Aparelhos conectados.</strong>
            </S.StepText>

            <S.StepText>
              3. Toque em <strong>Conectar um aparelho.</strong>
            </S.StepText>

            <S.StepText>4. Aponte seu celular para esta tela para capturar o código.</S.StepText>

            <S.StepText>
              5. Assim que validarmos a configuração, direcionaremos você <br />
              para a próxima etapa.
            </S.StepText>
          </S.StepsWrapper>
        </S.HeadingAndStepsWrapper>

        <S.QRCodeWrapper>
          {isLoadingImageURL && (
            <S.LoadingQRCodeWrapper>
              <MetroSpinner size={40} color={colors.green.default} loading={isLoadingImageURL} />

              <S.LoadingQRCodeWarn>
                Carregando QR Code. <br />
                Essa ação pode demorar alguns minutos.
              </S.LoadingQRCodeWarn>
            </S.LoadingQRCodeWrapper>
          )}

          {!isLoadingImageURL && (
            <S.QRCodeImage src={imageURL} alt="profitfy-whatsapp-qr-code-image" />
          )}
        </S.QRCodeWrapper>
      </S.Content>
    </S.Wrapper>
  );
};

export default AddWhatsappInstanceModal;
